import { Clock, Loader2, Replace, Video } from 'lucide-react'
import { useCallback, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { trpc } from '@/lib/trpc'
import { toastError, toastSuccess } from '@/lib/utils/toast-helpers'

interface YouTubeImportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sourceContentId: string
  onImported: () => void
}

interface PreviewVideo {
  title: string
  url: string
  description: string
  durationMinutes: number
  thumbnailUrl: string
  playlistOrder: number
  selected: boolean
  editedTitle: string
}

export function YouTubeImportModal({
  open,
  onOpenChange,
  sourceContentId,
  onImported,
}: YouTubeImportModalProps) {
  const [playlistUrl, setPlaylistUrl] = useState('')
  const [videos, setVideos] = useState<PreviewVideo[]>([])
  const [playlistThumbnailUrl, setPlaylistThumbnailUrl] = useState<
    string | null
  >(null)
  const [fetching, setFetching] = useState(false)
  const [importing, setImporting] = useState(false)
  const [findText, setFindText] = useState('')
  const [replaceText, setReplaceText] = useState('')
  const [truncateAfter, setTruncateAfter] = useState('')

  const fetchPlaylist = trpc.admin.content.fetchYouTubePlaylist.useQuery(
    { playlistUrl },
    { enabled: false },
  )

  const importPlaylist = trpc.admin.content.importYouTubePlaylist.useMutation()

  const handleFetch = useCallback(async () => {
    if (!playlistUrl.trim()) return
    setFetching(true)
    try {
      const result = await fetchPlaylist.refetch()
      if (result.data) {
        setVideos(
          result.data.videos.map((v) => ({
            ...v,
            selected: true,
            editedTitle: v.title,
          })),
        )
        setPlaylistThumbnailUrl(result.data.playlistThumbnailUrl ?? null)
      }
      if (result.error) {
        toastError(result.error, 'Failed to fetch playlist')
      }
    } catch (err) {
      toastError(err, 'Failed to fetch playlist')
    } finally {
      setFetching(false)
    }
  }, [playlistUrl, fetchPlaylist])

  const handleToggle = useCallback((index: number) => {
    setVideos((prev) =>
      prev.map((v, i) => (i === index ? { ...v, selected: !v.selected } : v)),
    )
  }, [])

  const handleToggleAll = useCallback(() => {
    setVideos((prev) => {
      const allSelected = prev.every((v) => v.selected)
      return prev.map((v) => ({ ...v, selected: !allSelected }))
    })
  }, [])

  const handleTitleChange = useCallback((index: number, title: string) => {
    setVideos((prev) =>
      prev.map((v, i) => (i === index ? { ...v, editedTitle: title } : v)),
    )
  }, [])

  const handleFindReplace = useCallback(() => {
    if (!findText) return
    setVideos((prev) =>
      prev.map((v) => ({
        ...v,
        editedTitle: v.editedTitle.replaceAll(findText, replaceText),
      })),
    )
  }, [findText, replaceText])

  const findMatchCount = findText
    ? videos.filter((v) => v.editedTitle.includes(findText)).length
    : 0

  const handleTruncateAfter = useCallback(() => {
    if (!truncateAfter) return
    setVideos((prev) =>
      prev.map((v) => {
        const idx = v.editedTitle.indexOf(truncateAfter)
        if (idx === -1) return v
        return { ...v, editedTitle: v.editedTitle.slice(0, idx).trimEnd() }
      }),
    )
  }, [truncateAfter])

  const truncateMatchCount = truncateAfter
    ? videos.filter((v) => v.editedTitle.includes(truncateAfter)).length
    : 0

  const handleClose = useCallback(() => {
    setPlaylistUrl('')
    setVideos([])
    setPlaylistThumbnailUrl(null)
    setFindText('')
    setReplaceText('')
    setTruncateAfter('')
    onOpenChange(false)
  }, [onOpenChange])

  const handleImport = useCallback(async () => {
    const selectedVideos = videos.filter((v) => v.selected)
    if (selectedVideos.length === 0) return

    setImporting(true)
    try {
      const result = await importPlaylist.mutateAsync({
        sourceContentId,
        playlistThumbnailUrl,
        videos: selectedVideos.map((v) => ({
          title: v.editedTitle,
          url: v.url,
          description: v.description,
          durationMinutes: v.durationMinutes,
          thumbnailUrl: v.thumbnailUrl,
        })),
      })
      toastSuccess(
        `Imported ${result.imported} video${result.imported !== 1 ? 's' : ''}`,
      )
      onImported()
      handleClose()
    } catch (err) {
      toastError(err, 'Failed to import playlist')
    } finally {
      setImporting(false)
    }
  }, [
    videos,
    importPlaylist,
    sourceContentId,
    onImported,
    handleClose,
    playlistThumbnailUrl,
  ])

  const selectedCount = videos.filter((v) => v.selected).length

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    return m > 0 ? `${h}h ${m}m` : `${h}h`
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[85vh] overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video size={20} />
            Import YouTube Playlist
          </DialogTitle>
          <DialogDescription>
            Paste a YouTube playlist URL to preview and import videos as
            resources.
          </DialogDescription>
        </DialogHeader>

        {/* URL Input */}
        <div className="flex gap-2">
          <input
            type="url"
            placeholder="https://www.youtube.com/playlist?list=..."
            value={playlistUrl}
            onChange={(e) => setPlaylistUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleFetch()
            }}
            className="border-border bg-background text-foreground placeholder:text-muted-foreground flex-1 rounded-md border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            disabled={fetching}
          />
          <Button
            onClick={handleFetch}
            disabled={fetching || !playlistUrl.trim()}
            size="sm"
          >
            {fetching ? (
              <>
                <Loader2 size={14} className="mr-1 animate-spin" />
                Fetching...
              </>
            ) : (
              'Fetch'
            )}
          </Button>
        </div>

        {/* Find & Replace */}
        {videos.length > 0 && (
          <div className="flex items-center gap-2">
            <Replace
              size={14}
              className="text-muted-foreground flex-shrink-0"
            />
            <input
              type="text"
              placeholder="Find..."
              value={findText}
              onChange={(e) => setFindText(e.target.value)}
              className="border-border bg-background text-foreground placeholder:text-muted-foreground min-w-0 flex-1 rounded-md border px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="text"
              placeholder="Replace with..."
              value={replaceText}
              onChange={(e) => setReplaceText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleFindReplace()
              }}
              className="border-border bg-background text-foreground placeholder:text-muted-foreground min-w-0 flex-1 rounded-md border px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleFindReplace}
              disabled={!findText || findMatchCount === 0}
              className="h-7 text-xs"
            >
              Replace{findMatchCount > 0 ? ` (${findMatchCount})` : ''}
            </Button>
          </div>
        )}

        {/* Truncate After */}
        {videos.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground flex-shrink-0 text-xs">
              Delete after
            </span>
            <input
              type="text"
              placeholder='e.g. " |"'
              value={truncateAfter}
              onChange={(e) => setTruncateAfter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTruncateAfter()
              }}
              className="border-border bg-background text-foreground placeholder:text-muted-foreground min-w-0 flex-1 rounded-md border px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-blue-500"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleTruncateAfter}
              disabled={!truncateAfter || truncateMatchCount === 0}
              className="h-7 text-xs"
            >
              Truncate{truncateMatchCount > 0 ? ` (${truncateMatchCount})` : ''}
            </Button>
          </div>
        )}

        {/* Preview Table */}
        {videos.length > 0 && (
          <div className="max-h-[50vh] overflow-y-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="w-8 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={videos.every((v) => v.selected)}
                      onChange={handleToggleAll}
                      className="rounded"
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-medium">Title</th>
                  <th className="w-20 px-3 py-2 text-right font-medium">
                    <Clock size={14} className="ml-auto inline" />
                  </th>
                </tr>
              </thead>
              <tbody className="divide-border divide-y">
                {videos.map((video, index) => (
                  <tr
                    key={video.url}
                    className={video.selected ? '' : 'bg-muted/20 opacity-50'}
                  >
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={video.selected}
                        onChange={() => handleToggle(index)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        value={video.editedTitle}
                        onChange={(e) =>
                          handleTitleChange(index, e.target.value)
                        }
                        className="bg-transparent w-full border-none p-0 text-sm outline-none focus:ring-0"
                        disabled={!video.selected}
                      />
                    </td>
                    <td className="text-muted-foreground px-3 py-2 text-right text-xs">
                      {formatDuration(video.durationMinutes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {videos.length > 0 && (
            <Button
              onClick={handleImport}
              disabled={importing || selectedCount === 0}
            >
              {importing ? (
                <>
                  <Loader2 size={14} className="mr-1 animate-spin" />
                  Importing...
                </>
              ) : (
                `Import ${selectedCount} Video${selectedCount !== 1 ? 's' : ''}`
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
